import React, { useEffect, useState } from 'react';
import { PricingTable, useAuth } from '@clerk/clerk-react';
import PageLayout from './PageLayout';
import Badge from './ui/Badge';
import { CheckIcon, MailIcon, ZapIcon, TrendingUpIcon, SparklesIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Button from './ui/Button';
import { CONTACT_URL, ADDITIONAL_SUBMISSIONS_PURCHASE_ENTRY } from '../constants';
import { useBillingClient } from '../billing/billing.client';
import { PageLoader } from './PageLoader';
const addOns = [{
        name: 'Additional Submissions',
        price: '$20',
        period: '',
        description: '1,000 more submission credits',
        icon: TrendingUpIcon,
        ...ADDITIONAL_SUBMISSIONS_PURCHASE_ENTRY,
    }];
const BillingPage = () => {
    const { has, isLoaded } = useAuth();
    const billingClient = useBillingClient();
    const [quotaStatus, setQuotaStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [purchasing, setPurchasing] = useState(false);
    useEffect(() => {
        loadQuotaStatus();
    }, []);
    const loadQuotaStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const status = await billingClient.getQuotaStatus();
            setQuotaStatus(status);
        }
        catch (err) {
            setError('Failed to load quota status');
        }
        finally {
            setLoading(false);
        }
    };
    // Check for success/cancel query params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            // Reload quota status after successful purchase
            setTimeout(() => {
                loadQuotaStatus();
                // Remove query param
                window.history.replaceState({}, '', window.location.pathname);
            }, 1000);
        }
    }, []);
    if (!isLoaded || loading) {
        return React.createElement(PageLoader, { text: "Loading billing information..." });
    }
    if (purchasing) {
        return React.createElement(PageLoader, { text: "Processing your purchase..." });
    }
    const hasProPlan = has({ plan: 'pro_plan' });
    const hasFreeOrgPlan = has({ plan: 'free_org' });
    const submissionsUsed = quotaStatus?.entities?.submission?.used || 0;
    const submissionsLimit = quotaStatus?.entities?.submission?.total || 0;
    const currentPlan = hasProPlan ? 'Pro' : hasFreeOrgPlan ? 'Free' : 'Unknown';
    const handleAdditionalPurchase = async (priceId, metadata) => {
        setPurchasing(true);
        setError(null);
        try {
            const successUrl = `${window.location.origin}/billing?success=true`;
            const cancelUrl = `${window.location.origin}/billing?canceled=true`;
            const result = await billingClient.makeAdditionalPurchase(priceId, metadata, successUrl, cancelUrl);
            if (result.success && result.checkoutUrl) {
                // Redirect to Stripe checkout
                window.location.href = result.checkoutUrl;
            }
            else {
                setError('Failed to create checkout session');
            }
        }
        catch (err) {
            setError('Failed to complete additional purchase');
        }
        finally {
            setTimeout(() => {
                setPurchasing(false);
            }, 1000);
        }
    };
    return (React.createElement(PageLayout, { title: "Billing & Subscription", subtitle: "Choose the plan that's right for you and your team", error: error },
        React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8" },
            React.createElement("div", { className: "flex items-center justify-between mb-6" },
                React.createElement("div", null,
                    React.createElement("h2", { className: "text-xl font-bold text-gray-900 mb-1" },
                        "You're on ",
                        currentPlan,
                        " Plan"),
                    React.createElement("p", { className: "text-sm text-gray-600" }, "Manage your subscription and usage")),
                React.createElement(Badge, { variant: "primary", className: "bg-orange-500 text-white" }, "Active")),
            React.createElement("div", { className: "grid grid-cols-1 gap-6" },
                React.createElement("div", null,
                    React.createElement("div", { className: "flex items-center justify-between mb-2" },
                        React.createElement("span", { className: "text-sm font-medium text-gray-700" }, "Submissions this cycle"),
                        React.createElement("span", { className: "text-sm text-gray-600" },
                            submissionsUsed,
                            " of ",
                            submissionsLimit)),
                    React.createElement("div", { className: "w-full bg-gray-200 rounded-full h-2.5" },
                        React.createElement("div", { className: "bg-blue-500 h-2.5 rounded-full transition-all duration-300", style: {
                                width: `${submissionsUsed / submissionsLimit * 100}%`
                            } }))))),
        React.createElement("h2", { className: "text-2xl font-bold text-gray-900 mb-6" }, "Choose Your Plan"),
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4" },
            React.createElement(PricingTable, { forOrganizations: true, appearance: {
                    variables: {
                        colorPrimary: '#f97316', // orange-500
                        colorBackground: '#ffffff',
                        // colorText: '#111827', // gray-900
                        // colorTextSecondary: '#4b5563', // gray-600
                        // colorInputBackground: '#ffffff',
                        // colorInputText: '#111827',
                        // borderRadius: '0.75rem', // rounded-xl (12px)
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        spacing: '1rem',
                    },
                    elements: {
                        pricingTableCardHeader: {
                            backgroundColor: 'white',
                        }
                    },
                    // elements: {
                    //   // Main container
                    //   rootBox: {
                    //     width: '100%',
                    //   },
                    //   // Plan cards - using CSS classes for hover states
                    //   pricingPlan: {
                    //     backgroundColor: '#ffffff',
                    //     borderRadius: '0.75rem',
                    //     border: '2px solid #e5e7eb',
                    //     padding: '1.5rem',
                    //     boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    //     transition: 'all 0.2s',
                    //   },
                    //   // Plan header
                    //   pricingPlanHeader: {
                    //     marginBottom: '1.5rem',
                    //   },
                    //   pricingPlanName: {
                    //     fontSize: '1.25rem',
                    //     fontWeight: '700',
                    //     color: '#111827',
                    //     marginBottom: '0.5rem',
                    //   },
                    //   pricingPlanPrice: {
                    //     fontSize: '2.25rem',
                    //     fontWeight: '700',
                    //     color: '#111827',
                    //     marginBottom: '0.5rem',
                    //   },
                    //   pricingPlanPricePeriod: {
                    //     fontSize: '1rem',
                    //     color: '#4b5563',
                    //     marginLeft: '0.25rem',
                    //   },
                    //   pricingPlanDescription: {
                    //     fontSize: '0.875rem',
                    //     color: '#4b5563',
                    //   },
                    //   // Features list
                    //   pricingPlanFeatures: {
                    //     marginBottom: '1.5rem',
                    //     gap: '0.75rem',
                    //   },
                    //   pricingPlanFeature: {
                    //     fontSize: '0.875rem',
                    //     color: '#374151',
                    //     display: 'flex',
                    //     alignItems: 'flex-start',
                    //     gap: '0.5rem',
                    //   },
                    //   pricingPlanFeatureIcon: {
                    //     color: '#22c55e',
                    //     width: '1.25rem',
                    //     height: '1.25rem',
                    //     flexShrink: 0,
                    //     marginTop: '0.125rem',
                    //   },
                    //   // Badge for popular plan
                    //   pricingPlanBadge: {
                    //     backgroundColor: '#f97316',
                    //     color: '#ffffff',
                    //     borderRadius: '9999px',
                    //     padding: '0.25rem 0.75rem',
                    //     fontSize: '0.75rem',
                    //     fontWeight: '600',
                    //     boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    //     position: 'absolute',
                    //     top: '-1rem',
                    //     left: '50%',
                    //     transform: 'translateX(-50%)',
                    //   },
                    //   // CTA Buttons
                    //   pricingPlanCta: {
                    //     width: '100%',
                    //     borderRadius: '0.5rem',
                    //     padding: '0.625rem 1rem',
                    //     fontSize: '0.875rem',
                    //     fontWeight: '500',
                    //     transition: 'all 0.2s',
                    //   },
                    //   // Section headers
                    //   pricingSectionHeader: {
                    //     fontSize: '1.5rem',
                    //     fontWeight: '700',
                    //     color: '#111827',
                    //     marginBottom: '1.5rem',
                    //   },
                    //   pricingSectionDescription: {
                    //     fontSize: '1rem',
                    //     color: '#4b5563',
                    //     marginBottom: '1.5rem',
                    //   },
                    // },
                } }),
            [
                {
                    name: 'Enterprise',
                    price: 'Custom',
                    period: 'Pricing',
                    description: 'Advanced capabilities for large organizations',
                    features: ['Everything in Pro', 'Custom pricing', 'Advanced voice & agent capabilities', 'Custom integrations', 'Priority support & SLA'],
                    cta: 'Contact Sales',
                    ctaVariant: 'secondary',
                    highlighted: false
                }
            ].map((plan, index) => React.createElement("div", { key: plan.name, className: `animate-fade-in-up bg-white rounded-xl border-2 ${plan.highlighted ? 'border-orange-300' : 'border-gray-200'} hover:shadow-lg transition-all` },
                React.createElement("div", { className: "mb-2", style: {
                        padding: '1rem',
                        display: 'grid',
                        gap: '0.25rem',
                        gridRow: 'span 3',
                        gridTemplateRows: 'subgrid',
                    } },
                    React.createElement("h3", { className: "text-xl font-bold text-gray-900 mb-2" }, plan.name),
                    React.createElement("div", { className: "flex items-baseline mb-2" },
                        React.createElement("span", { className: "text-4xl font-bold text-gray-900" }, plan.price),
                        React.createElement("span", { className: "text-gray-600 ml-1" }, plan.period)),
                    React.createElement("p", { className: "text-sm text-gray-600" }, plan.description)),
                React.createElement("ul", { className: "mb-4 p-4" }, plan.features.map((feature, idx) => React.createElement("li", { key: idx, className: "flex items-start text-sm" },
                    React.createElement(CheckIcon, { className: "w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" }),
                    React.createElement("span", { className: "text-gray-700" }, feature)))),
                React.createElement("div", { className: "p-4" },
                    React.createElement(Button, { variant: plan.ctaVariant, size: "md", className: "w-full", disabled: plan.highlighted, onClick: () => {
                            window.open(CONTACT_URL, '_blank');
                        } }, plan.cta))))),
        React.createElement("div", { className: "mb-12 mt-12" },
            React.createElement("h2", { className: "text-2xl font-bold text-gray-900 mb-2" }, "Add-Ons"),
            React.createElement("p", { className: "text-gray-600 mb-6" }, "Enhance your plan with additional capabilities"),
            React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" }, addOns.map((addOn, index) => {
                const Icon = addOn.icon;
                return React.createElement("div", { key: addOn.name, style: { animationDelay: `${index * 0.1}s` }, className: "animate-fade-in-up bg-white rounded-xl border border-gray-200 p-6 hover:border-orange-300 hover:shadow-md transition-all" },
                    React.createElement("div", { className: "flex items-start justify-between mb-4" },
                        React.createElement("div", { className: "w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center" },
                            React.createElement(Icon, { className: "w-6 h-6 text-orange-600" })),
                        React.createElement("div", { className: "text-right" },
                            React.createElement("div", { className: "text-2xl font-bold text-gray-900" }, addOn.price),
                            React.createElement("div", { className: "text-sm text-gray-600" }, addOn.period))),
                    React.createElement("h3", { className: "text-lg font-bold text-gray-900 mb-2" }, addOn.name),
                    React.createElement("p", { className: "text-sm text-gray-600 mb-4" }, addOn.description),
                    React.createElement(Button, { variant: "primary", size: "sm", className: "w-full", onClick: () => {
                            handleAdditionalPurchase(addOn.priceId, { entity: addOn.entity, quantity: addOn.quantity });
                        } }, "Upgrade Now"));
            })),
            React.createElement(AdvancedAddOns, null)),
        React.createElement("div", { className: "mb-12 mt-12" },
            React.createElement("h2", { className: "text-2xl font-bold text-gray-900 mb-2" }, "Invoices for One Time Payments"),
            React.createElement("p", { className: "text-gray-600 mb-6" }, "View and download your invoices from Stripe"),
            React.createElement(InvoicesTable, null))));
};
const advancedAddOns = [{
        name: 'AI Document Validation',
        description: 'Advanced document verification and validation',
        icon: CheckIcon,
    }, {
        name: 'Voice & "Call-Us" Form Experiences',
        description: 'Enable voice-based form completion',
        icon: SparklesIcon,
    }, {
        name: 'Fluid Sales & Intake Agents',
        description: 'Deploy AI agents for complex workflows',
        icon: ZapIcon,
    }, {
        name: 'Email-to-Form & Ad-Hoc Document Intake',
        description: 'Email documents directly into forms',
        icon: MailIcon,
    }];
function AdvancedAddOns() {
    return (React.createElement("div", { className: "bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 p-6" },
        React.createElement("div", { className: "flex items-center justify-between mb-6" },
            React.createElement("div", null,
                React.createElement("h3", { className: "text-xl font-bold text-gray-900 mb-1" }, "Advanced Add-Ons"),
                React.createElement("p", { className: "text-sm text-gray-600" }, "Enterprise-grade features for complex workflows")),
            React.createElement(Badge, { variant: "primary", className: "bg-orange-500 text-white text-xs" }, "Contact Required")),
        React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, advancedAddOns.map(addOn => {
            const Icon = addOn.icon;
            return React.createElement("a", { key: addOn.name, href: CONTACT_URL, target: "_blank", className: "bg-white rounded-lg border border-orange-200 p-4 hover:border-orange-400 hover:shadow-md transition-all cursor-pointer" },
                React.createElement("div", { className: "flex items-start" },
                    React.createElement("div", { className: "w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" },
                        React.createElement(Icon, { className: "w-5 h-5 text-orange-600" })),
                    React.createElement("div", null,
                        React.createElement("h4", { className: "font-semibold text-gray-900 text-sm mb-1" }, addOn.name),
                        React.createElement("p", { className: "text-xs text-gray-600" }, addOn.description))));
        }))));
}
/**
 * Invoices table component displaying all Stripe invoices for the organization
 */
function InvoicesTable() {
    const billingClient = useBillingClient();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [lastInvoiceId, setLastInvoiceId] = useState(undefined);
    const [firstInvoiceId, setFirstInvoiceId] = useState(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const loadInvoices = async (startingAfter, endingBefore) => {
        try {
            setLoading(true);
            setError(null);
            const result = await billingClient.getInvoices(startingAfter, endingBefore);
            setInvoices(result.invoices);
            setHasMore(result.hasMore);
            setLastInvoiceId(result.lastInvoiceId);
            setFirstInvoiceId(result.firstInvoiceId);
        }
        catch (err) {
            setError('Failed to load invoices');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadInvoices(undefined, undefined);
    }, [billingClient]);
    const handleNext = () => {
        if (lastInvoiceId && hasMore) {
            loadInvoices(lastInvoiceId, undefined);
            setCurrentPage(prev => prev + 1);
        }
    };
    const handlePrevious = () => {
        // Use ending_before with the first invoice ID of current page to get previous page
        if (firstInvoiceId && currentPage > 1) {
            loadInvoices(undefined, firstInvoiceId);
            setCurrentPage(prev => prev - 1);
        }
    };
    if (loading) {
        return (React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm p-6" },
            React.createElement("div", { className: "text-center py-8" },
                React.createElement("div", { className: "text-gray-500" }, "Loading invoices..."))));
    }
    if (error) {
        return (React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm p-6" },
            React.createElement("div", { className: "text-center py-8" },
                React.createElement("div", { className: "text-red-600 mb-2" }, error),
                React.createElement(Button, { variant: "secondary", size: "sm", onClick: () => {
                        loadInvoices(undefined, undefined);
                    } }, "Retry"))));
    }
    const formatTotal = (total, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(total / 100);
    };
    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };
    const getStatusBadge = (status) => {
        const statusMap = {
            paid: { variant: 'success', className: 'bg-green-100 text-green-800', label: 'Paid' },
            open: { variant: 'warning', className: 'bg-yellow-100 text-yellow-800', label: 'Open' },
            draft: { variant: 'default', className: 'bg-gray-100 text-gray-800', label: 'Draft' },
            uncollectible: { variant: 'danger', className: 'bg-red-100 text-red-800', label: 'Uncollectible' },
            void: { variant: 'default', className: 'bg-gray-100 text-gray-800', label: 'Void' },
        };
        const statusInfo = statusMap[status] || { variant: 'default', className: 'bg-gray-100 text-gray-800', label: status };
        return (React.createElement(Badge, { variant: statusInfo.variant, className: statusInfo.className }, statusInfo.label));
    };
    return (React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" },
        React.createElement("div", { className: "px-6 py-4 border-b border-gray-200 bg-gray-50" },
            React.createElement("div", { className: "flex items-center justify-between" },
                React.createElement("div", null,
                    React.createElement("h3", { className: "text-lg font-semibold text-gray-900" }, "Invoices")))),
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement("table", { className: "min-w-full divide-y divide-gray-200" },
                React.createElement("thead", { className: "bg-gray-50" },
                    React.createElement("tr", null,
                        React.createElement("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Invoice #"),
                        React.createElement("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Date"),
                        React.createElement("th", { className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Due Date"),
                        React.createElement("th", { className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Total"),
                        React.createElement("th", { className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Status"),
                        React.createElement("th", { className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Actions"))),
                React.createElement("tbody", { className: "bg-white divide-y divide-gray-200" }, invoices.length === 0 ? (React.createElement("tr", null,
                    React.createElement("td", { colSpan: 6, className: "px-6 py-8 text-center text-gray-500" }, "No invoices found"))) : (invoices.map((invoice) => (React.createElement("tr", { key: invoice.id, className: "hover:bg-gray-50 transition-colors" },
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" }, invoice.number || invoice.id.substring(0, 12)),
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }, formatDate(invoice.created)),
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-sm text-gray-600" }, formatDate(invoice.dueDate)),
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900" }, formatTotal(invoice.total, invoice.currency)),
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-right" }, getStatusBadge(invoice.status)),
                    React.createElement("td", { className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium" }, invoice.hostedInvoiceUrl ? (React.createElement("a", { href: invoice.hostedInvoiceUrl, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 hover:text-blue-800" }, "View")) : ('—'))))))))),
        (hasMore || !(currentPage <= 1)) && (React.createElement("div", { className: "px-6 py-4 border-t border-gray-200 bg-gray-50" },
            React.createElement("div", { className: "flex items-center justify-between" },
                React.createElement("div", { className: "text-sm text-gray-700" },
                    "Showing ",
                    invoices.length,
                    " invoice",
                    invoices.length !== 1 ? 's' : '',
                    currentPage > 1 && ` • Page ${currentPage}`),
                React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement(Button, { variant: "secondary", size: "sm", onClick: handlePrevious, disabled: currentPage <= 1 || loading, className: "flex items-center gap-1" },
                        React.createElement(ChevronLeftIcon, { className: "w-4 h-4" }),
                        "Previous"),
                    React.createElement(Button, { variant: "secondary", size: "sm", onClick: handleNext, disabled: !hasMore || loading, className: "flex items-center gap-1" },
                        "Next",
                        React.createElement(ChevronRightIcon, { className: "w-4 h-4" }))))))));
}
export default BillingPage;
